import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../src/Context/AuthContext";

const UploadServiceForms = ({
    refNo,
    refNoType,
    onUploadComplete = () => { }
}) => {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [previewUrl, setPreviewUrl] = useState(null);

    const fileInputRef = useRef(null);
    const apiURL = import.meta.env.VITE_API;

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        setError("");
        setSuccess("");

        // Clean up prior preview URL
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }

        if (!file) {
            setSelectedFile(null);
            return;
        }

        // Basic client-side size limit
        const maxBytes = 10 * 1024 * 1024; // 10MB
        if (file.size > maxBytes) {
            setError("File is too large. Maximum size is 10 MB.");
            setSelectedFile(null);
            return;
        }

        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "image/gif",
            "image/tiff",
            "application/pdf"
        ];

        if (!allowedTypes.includes(file.type)) {
            setError("Unsupported file type. Please upload a JPG, PNG, TIFF, GIF, or PDF.");
            setSelectedFile(null);
            return;
        }

        setSelectedFile(file);

        // Always create preview URL – iframe can handle both images & PDFs
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!refNo) {
            setError("Missing reference number for upload.");
            return;
        }

        if (!selectedFile) {
            setError("Please select a file to upload.");
            return;
        }

        try {
            setUploading(true);

            const formData = new FormData();
            formData.append("refNo", refNo);
            formData.append("refNoType", refNoType);
            formData.append("file", selectedFile);

            const res = await fetch(`${apiURL}/image/uploadServiceForm`, {
                method: "POST",
                credentials: "include",
                body: formData
            });

            if (res.status === 401) {
                logout();
                navigate("/login");
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate("/home");
                throw new Error("forbidden");
            }

            if (!res.ok) {
                const text = await res.text();
                console.error("Upload failed:", text);
                throw new Error("Upload failed");
            }

            setSuccess("File uploaded successfully.");
            setSelectedFile(null);

            // Clear file input element
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }

            // Clear preview
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
            }

            onUploadComplete();
        } catch (err) {
            console.error("Error uploading service form:", err);
            setError("Error uploading file. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="upload-service-forms-container">
            <h3 className="profile-header">Upload Service Form</h3>
            <div className="profile-label baseText">
                Acceptable file types include .pdf, .jpg, .jpeg, .png, .tif, and .tiff. 
            </div>
            <div className="upload-service-forms-layout">
                
                <form className="upload-form" onSubmit={handleSubmit}>
                    <div className="upload-row">
                        <label className="profile-label">Reference No:</label>
                        <span className="profile-value">{refNo || "N/A"}</span>
                    </div>

                    <div className="upload-row">
                        <label className="profile-label" htmlFor="fileUpload">
                            File:
                        </label>
                        <input
                            id="fileUpload"
                            type="file"
                            className="filterInput"
                            onChange={handleFileChange}
                            accept=".jpg,.jpeg,.png, .tif,.tiff,.pdf"
                            ref={fileInputRef}
                        />
                    </div>

                    {error && (
                        <div className="upload-message error">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="upload-message success">
                            {success}
                        </div>
                    )}

                    <div className="profile-actions" style={{ marginTop: "1rem" }}>
                        <button
                            type="submit"
                            className="baseButton"
                            disabled={uploading}
                        >
                            {uploading ? "Uploading..." : "Upload"}
                        </button>
                    </div>
                </form>

                
            </div>
            {previewUrl && (
                <div className="upload-preview-panel">
                    <div className="upload-preview-title">Preview</div>

                    <iframe
                        title="File preview"
                        src={previewUrl}
                        className="upload-preview-iframe"
                    />
                </div>
            )}
        </div>
    );
};

export default UploadServiceForms;
